/**
 * <mk-cart-recommendations> — Cross-Selling im Warenkorb-Schieber.
 *
 * Der Schieber kennt kein einzelnes Produkt, Shopifys Empfehlungs-Route braucht
 * aber eines. Bezugspunkt ist deshalb das teuerste Stück im Warenkorb (Stefan
 * 2026-08-08) — meist das, wegen dem der Kunde gekommen ist. Liquid ermittelt es
 * und reicht es als data-anchor-product-id herein; das erspart einen eigenen
 * /cart.js-Abruf und ist nach jedem Neuaufbau des Schiebers automatisch aktuell.
 *
 * Zuerst werden die im Admin gepflegten „Ergänzenden Produkte" abgefragt. Liefert
 * das nichts — aktuell ist dort nichts hinterlegt —, greift Shopifys automatische
 * Verwandtschaft. Beides über dieselbe schlanke Section
 * (sections/mk-cart-recommendations.liquid), die auch gleich herausfiltert, was
 * schon im Warenkorb liegt.
 */
import { ThemeEvents } from '@theme/events';

const INTENTS = ['complementary', 'related'];

class MkCartRecommendations extends HTMLElement {
  #controller = null;

  connectedCallback() {
    this.#load();
    // Nach dem Hinzufügen aus dem Schieber heraus ändert sich der Warenkorb, ohne
    // dass dieses Element zwingend neu aufgebaut wird — dann selbst nachladen,
    // damit der gerade gekaufte Artikel aus den Vorschlägen verschwindet.
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartChange);
    // Am Element selbst, nicht an der Karte: die Karte wird bei jedem Nachladen ersetzt.
    this.addEventListener('change', this.#onVariantChange);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartChange);
    this.removeEventListener('change', this.#onVariantChange);
    this.#controller?.abort();
  }

  #onCartChange = () => {
    this.#load();
  };

  /**
   * Grundpreis-Zeile auf die gewählte Variante nachführen (PAngV). Den Wert je Option
   * rendert die Section in `data-mk-unit-price`; ohne Grundpreis wird die Zeile verborgen.
   */
  #onVariantChange = (event) => {
    const select = event.target.closest?.('[data-mk-cart-recos-variant]');
    if (!select) return;
    const line = this.querySelector('[data-mk-cart-recos-unit]');
    const value = line?.querySelector('[data-mk-cart-recos-unit-value]');
    if (!line || !value) return;
    const unitPrice = select.selectedOptions[0]?.dataset.mkUnitPrice ?? '';
    value.textContent = unitPrice;
    line.hidden = !unitPrice;
  };

  async #load() {
    const productId = (this.dataset.anchorProductId || '').trim();
    const sectionId = (this.dataset.sectionId || '').trim();
    if (!productId || !sectionId) return this.#clear();

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    try {
      for (const intent of INTENTS) {
        const markup = await this.#fetchSection(productId, sectionId, intent, signal);
        if (markup && this.#mount(markup)) return;
      }
      this.#clear();
    } catch (error) {
      if (error?.name !== 'AbortError') this.#clear();
    }
  }

  async #fetchSection(productId, sectionId, intent, signal) {
    const url = new URL(
      window.Theme?.routes?.product_recommendations_url || '/recommendations/products',
      window.location.origin
    );
    url.searchParams.set('product_id', productId);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('intent', intent);
    // Großzügig anfragen: die Section wirft heraus, was schon im Warenkorb
    // liegt, und soll danach immer noch genug zum Zeigen haben.
    url.searchParams.set('limit', '10');

    const response = await fetch(url, { signal });
    if (!response.ok) return null;
    return response.text();
  }

  /** @returns {boolean} true, wenn tatsächlich Vorschläge eingehängt wurden */
  #mount(markup) {
    const doc = new DOMParser().parseFromString(markup, 'text/html');
    const fresh = doc.querySelector('[data-mk-cart-recos-inner]');
    if (!fresh || fresh.children.length === 0) return false;

    // Das CSS dieser Section und des Quick-Adds gibt es NUR in dieser Antwort:
    // im Schieber wird nichts davon serverseitig gerendert, also subsettet
    // Shopify es aus dem Seiten-Bundle heraus. Der <style> steht NEBEN dem
    // Container — ohne ihn kämen die Vorschläge völlig ungestylt an.
    const styles = [...doc.querySelectorAll('style')].filter((el) => !fresh.contains(el));

    // Den Container MIT einhängen, nicht nur seine Kinder: an ihm hängen Abstand
    // und Layout der Section. Nur die Kinder zu übernehmen ließ jede Regel auf
    // `.mk-cart-recos` ins Leere laufen — Überschrift und Karte klebten am Rand.
    this.replaceChildren(...styles, fresh);
    return true;
  }

  // Leer heißt verborgen (:empty in mk-cart-recos-styles). Ein hidden-Attribut setzte
  // jeder Morph des Schiebers aus dem SSR-Markup wieder, siehe header-actions.liquid.
  #clear() {
    this.replaceChildren();
  }
}

if (!customElements.get('mk-cart-recommendations')) {
  customElements.define('mk-cart-recommendations', MkCartRecommendations);
}
